import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { cn, formatIDR, hexToRgba } from '@/lib/utils';
import { ThemeColorSchemeType, ThemePromotionType } from '@/stores/builder';
import { BaseDiscountReward, BasePromotion } from '@/types/http/promotion/common';
import { BadgePercent, Info, X } from 'lucide-react';

type CardVoucherProps = {
  promo: BasePromotion & { total_discount?: number };
  percentage?: number;
  minSpending: number;
  grandTotal?: number;
  onApply?: (promo: BasePromotion) => void;
  onRemove?: (promo: string) => void;
  isEligible?: boolean;
  isRemove?: boolean;
  isShowReward?: boolean;
  isCartDrawer?: boolean;
  themeCart?: ThemeColorSchemeType;
  theme?: ThemePromotionType;
};

export default function CardVoucher({
  promo,
  percentage,
  minSpending,
  grandTotal,
  onApply,
  onRemove,
  isEligible = false,
  isRemove,
  isShowReward = false,
  isCartDrawer = false,
  themeCart,
  theme,
}: CardVoucherProps) {
  const isEligiblePromo = (grandTotal || 0) >= minSpending;

  const handleClick = () => {
    if (isRemove) {
      onRemove?.(promo.promotion_id);
    } else {
      onApply?.(promo);
    }
  };

  const calculateDiscount = (grandTotal: number, discount: BaseDiscountReward) => {
    let rawDiscount = 0;

    if (discount?.discount_type === 'percentage') {
      rawDiscount = (discount?.discount_amount / 100) * grandTotal;
    } else if (discount?.discount_type === 'amount') {
      rawDiscount = discount?.discount_amount;
    }

    if (discount?.max_discount_amount > 0) {
      rawDiscount = Math.min(rawDiscount, discount?.max_discount_amount);
    }

    return Math.ceil(rawDiscount);
  };

  const hasDiscountReward =
    Array.isArray(promo.discount_reward) && promo.discount_reward.length > 0;

  const discountText = hasDiscountReward
    ? `Saved ${formatIDR(
        promo?.total_discount != null
          ? Math.ceil(promo.total_discount)
          : calculateDiscount(grandTotal ?? 0, promo.discount_reward![0])
      )}`
    : '';

  const rewardText = discountText;
  const tnc = promo.promotion_tnc;

  return (
    <div
      className='relative mb-4 w-full items-center overflow-hidden rounded-lg px-4 py-4 text-black'
      style={{ background: hexToRgba(theme?.colorScheme?.background as string, 0.1) }}
    >
      <div
        className='absolute -left-4 top-1/2 z-10 h-10 w-6 -translate-y-1/2 rounded-full'
        style={{ background: themeCart?.background }}
      />
      <div
        className='absolute -right-4 top-1/2 z-10 h-10 w-6 -translate-y-1/2 rounded-full'
        style={{ background: themeCart?.background }}
      />

      <div className='flex w-full justify-between gap-4'>
        <div className='flex items-center gap-3'>
          <BadgePercent className='size-7' style={{ color: theme?.colorScheme?.text }} />
          <div className='flex flex-col text-sm'>
            <h3
              className='line-clamp-1 text-base font-semibold'
              title={promo.promotion_name}
              style={{ color: theme?.colorScheme?.text }}
            >
              {promo.promotion_name}
            </h3>
            {!(tnc?.typeSpending === 'product' && tnc?.minPurchase === 0) && (
              <p className='text-xs' style={{ color: theme?.colorScheme?.textSecondary }}>
                Min. Purchase{' '}
                {tnc?.typeSpending === 'amount' || promo.minimum_spending > 1000
                  ? `${formatIDR(promo.minimum_spending)}`
                  : `${promo.minimum_spending} pcs`}
              </p>
            )}
            {((isCartDrawer && isShowReward) || rewardText) && (
              <span
                className={cn('text-sm', !isCartDrawer && 'lg:hidden')}
                style={{ color: theme?.colorScheme?.text }}
              >
                {rewardText}
              </span>
            )}
          </div>
        </div>

        <div className={cn('flex items-center gap-3')}>
          {isShowReward && rewardText && (
            <span
              className={cn('hidden text-sm', !isCartDrawer ? 'lg:flex' : 'text-[10px]')}
              style={{ color: theme?.colorScheme?.text }}
            >
              {rewardText}
            </span>
          )}

          {isRemove && (
            <Button
              variant={null}
              className={cn(
                'px-3 py-1 font-semibold',
                (!isRemove || (isEligible && !isEligiblePromo)) && '!cursor-not-allowed'
              )}
              style={{ color: theme?.colorScheme?.btnSolidBackgroud }}
              disabled={!isRemove || (isEligible && !isEligiblePromo)}
              onClick={handleClick}
            >
              <X className='h-5 w-5' />
            </Button>
          )}
        </div>
      </div>

      {isEligible && (
        <>
          <div className='mt-3 h-2 w-full overflow-hidden rounded-full bg-gray-200'>
            <div
              className='h-2 transition-all duration-300'
              style={{ width: `${percentage}%`, background: theme?.progressBar }}
            />
          </div>
          <div className='mt-2'>
            {isEligiblePromo ? (
              <Badge
                variant='outline'
                className='inline-flex items-center gap-1.5 rounded border-orange-300 bg-orange-100/50 px-2 py-1 text-xs font-medium text-orange-700'
              >
                Ready to Claim
              </Badge>
            ) : (
              <Badge
                variant='outline'
                className='inline-flex items-center gap-1.5 rounded border-yellow-300 bg-yellow-100/50 px-2 py-1 text-xs font-medium text-yellow-800'
              >
                <Info className='h-4 w-4' />
                Spend {formatIDR(minSpending - (grandTotal ?? 0))} more to get promotion
              </Badge>
            )}
          </div>
        </>
      )}
    </div>
  );
}
